import { type FC, useContext } from 'react';
import classnames from 'classnames';
import { graphql, useFragment } from 'react-relay';

import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { GetHelpZendeskContactBuyer } from './GetHelpZendeskContactBuyer.tsx';
import { GetHelpZendeskSellerItemTile } from './GetHelpZendeskSellerItemTile.tsx';
import { GetHelpZendeskSellerOrderTile } from './GetHelpZendeskSellerOrderTile.tsx';

import { trackGetHelpZendeskEvent } from './getHelpZendeskTracking.ts';
import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';

import { type GetHelpZendeskCmsTopic_viewer$key } from './__generated__/GetHelpZendeskCmsTopic_viewer.graphql.ts';

import dibsCss from 'dibs-css';
import styles from './GetHelpZendeskTopic.scss';

const viewerFragment = graphql`
    fragment GetHelpZendeskCmsTopic_viewer on Viewer {
        getHelpTopic(topicKey: $topicKey, orderId: $orderId, isSeller: $isSeller) {
            label
            instructionalText
            primaryCta {
                label
                link
            }
            supportCenterLink
            showContactButton
            ...UpdatedContactSellerForm_getHelpTopic
        }
        ...ItemReturnPolicy_viewer
        ...TrackingDetails_viewer
        ...GetHelpZendeskContactBuyer_viewer
        ...GetHelpZendeskSellerItemTile_viewer
        ...GetHelpZendeskSellerOrderTile_viewer
    }
`;

const GetHelpZendeskCmsTopic: FC<{ viewer: GetHelpZendeskCmsTopic_viewer$key }> = ({
    viewer: viewerRef,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const {
        state: { isSeller, topicKey, itemId, orderId },
    } = useContext(GetHelpZendeskContext);

    const { getHelpTopic } = viewer;
    const { label, instructionalText, primaryCta, supportCenterLink, showContactButton } =
        getHelpTopic || {};
    const { label: primaryCtaLabel, link: primaryCtaLink } = primaryCta || {};

    return (
        <>
            {isSeller && itemId && (
                <div className={dibsCss.mbMedium}>
                    <GetHelpZendeskSellerItemTile viewer={viewer} />
                </div>
            )}
            {isSeller && orderId && (
                <div className={dibsCss.mbMedium}>
                    <GetHelpZendeskSellerOrderTile viewer={viewer} />
                </div>
            )}
            <div className={dibsCss.sassyFontHeaderLarge}>{label}</div>
            <div
                className={classnames(
                    styles.instructionalText,
                    dibsCss.mtSmall,
                    dibsCss.sassyFontBody
                )}
                dangerouslySetInnerHTML={{
                    __html: instructionalText || '',
                }}
            />
            {primaryCta && (
                <div className={dibsCss.mtSmall}>
                    <Button
                        href={primaryCtaLink || ''}
                        onClick={() =>
                            trackGetHelpZendeskEvent({
                                stepInteractionName: 'CTA clicked',
                                trigger: primaryCtaLabel || '',
                                isSeller,
                            })
                        }
                        opensInNewWindow
                        size="medium"
                    >
                        {primaryCtaLabel}
                    </Button>
                </div>
            )}
            {isSeller && showContactButton && <GetHelpZendeskContactBuyer viewer={viewer} />}
            {supportCenterLink && (
                <div className={dibsCss.mtSmall}>
                    <Link
                        href={supportCenterLink || ''}
                        target="_blank"
                        onClick={() =>
                            trackGetHelpZendeskEvent({
                                stepInteractionName: 'learn more clicked',
                                trigger: topicKey,
                                isSeller,
                            })
                        }
                    >
                        <span className={dibsCss.sassyFontBody}>
                            <FormattedMessage
                                id="getHelp.GetHelpZendeskCmsTopic.supportCenterLink"
                                defaultMessage="Learn more on Support Center"
                            />
                        </span>
                    </Link>
                </div>
            )}
        </>
    );
};

export { GetHelpZendeskCmsTopic };
